import { cn } from "@/lib/utils";
import type { PrimeAccent } from "@/content/prime-services";


type Props = {
  images: string[];
  accent?: PrimeAccent;
  eyebrow?: string;
  title?: string;
  lede?: string;
};

export function StillsMarquee({
  images,
  accent = "mint",
  eyebrow = "Stillbilder",
  title = "Bakom kamerorna",
  lede = "Ett axplock från färska produktioner — set, ljus och människorna framför linsen.",
}: Props) {
  if (!images || images.length < 3) return null;
  const loop = [...images, ...images];
  const accentClass =
    accent === "peach"
      ? "text-peach"
      : accent === "butter"
      ? "text-butter"
      : "text-mint";

  return (
    <section aria-label={title} className="border-y border-white/10 bg-surface/30 py-16 md:py-20">
      <div className="container-wide mb-10 md:mb-12 max-w-4xl">
        <div className={cn("mb-4 text-xs uppercase tracking-[0.18em]", accentClass)}>
          {eyebrow}
        </div>
        <h2 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
          {title}
        </h2>
        <p className="mt-6 text-pretty text-base text-muted-foreground md:text-lg">
          {lede}
        </p>
      </div>

      <div className="stills-marquee-mask group/marquee relative overflow-hidden py-1">
        <div className="stills-marquee flex w-max gap-3 md:gap-4" aria-hidden="true">
          {loop.map((src, i) => (
            <div
              key={i}
              className={cn(
                "h-40 w-[260px] shrink-0 overflow-hidden rounded-xl bg-surface md:h-56 md:w-[400px] md:rounded-2xl lg:h-64 lg:w-[460px]"
              )}
            >
              <img
                src={src}
                 alt=""
                loading="lazy"
                decoding="async"
                draggable={false}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes stillsMarquee {
          from { transform: translate3d(0,0,0); }
          to   { transform: translate3d(-50%,0,0); }
        }
        .stills-marquee {
          animation: stillsMarquee var(--stills-duration, 40s) linear infinite;
          will-change: transform;
          backface-visibility: hidden;
        }
        .group\\/marquee:hover .stills-marquee { animation-play-state: paused; }
        .stills-marquee-mask {
          -webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
                  mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
        }
        @media (min-width: 768px)  { .stills-marquee { --stills-duration: 60s; } }
        @media (min-width: 1280px) { .stills-marquee { --stills-duration: 80s; } }
        @media (prefers-reduced-motion: reduce) {
          .stills-marquee { animation: none; transform: none; }
        }
      `}</style>
    </section>
  );
}
